/**
 * PF v2's own embed seam — the replacement for legacy's `EmbedConfig.context`.
 *
 * Deliberately far smaller than legacy's. That context also carried
 * `revenueChannel` and `isOneSystem`, derived from the URL and threaded into
 * every catalogue call; v2 must NOT reproduce that. The channel is declared once
 * at session-create and the backend derives the pricebook and the order tag from
 * the session, so the front end stays channel-blind and cannot
 * disagree with what the order is actually billed.
 *
 * What is left is what the client sees: whether to render the iframe's chrome,
 * the language Propstack forces through `?lang`, and the CRM property the
 * iframe was opened for.
 */

import { useMemo } from 'react'

import { Path } from 'constants/router'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import { useEmbedLocaleSelfHeal } from 'utils/hooks/useEmbedLocaleSelfHeal'

import { rememberEmbedLocale } from './embedLocale'
import { rememberEmbedPropertyRef } from './embedPropertyRef'
import { usePfRootPath } from './pfBasePath'

export interface PfEmbed {
  /** The flow is rendering inside the CRM iframe. */
  isEmbed: boolean
  /** Language the iframe was opened in, or null when it forced none. Survives step navigation. */
  locale: string | null
  /** Propstack's `objectReferenceId` for the property being ordered, or null outside the embed. Survives step navigation. */
  propertyRef: string | null
}

/**
 * @example
 * const { isEmbed } = usePfEmbed()
 */
export const usePfEmbed = (): PfEmbed => {
  const params = useBkbnSearchParams()
  const isEmbed = usePfRootPath() === Path.EMBED_PURCHASE_FLOW
  // Remembered rather than read fresh: every in-flow navigation drops the search string, so from
  // the second screen on the parameter is gone and the self-heal below would silently disarm.
  const locale = useMemo(() => rememberEmbedLocale(params.get('lang')), [params])
  const rememberedRef = useMemo(() => rememberEmbedPropertyRef(params.get('objectReferenceId')), [params])
  const propertyRef = isEmbed ? rememberedRef : null

  // The forced language is applied at boot by the language detector, but an
  // in-app navigation re-applies the profile language and nothing re-forces it.
  // Passing `isEmbed` rather than gating the call keeps the hook order stable.
  useEmbedLocaleSelfHeal(isEmbed, locale)

  return useMemo(() => ({ isEmbed, locale, propertyRef }), [isEmbed, locale, propertyRef])
}
